import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Scale, Play, ChevronRight, X, Clock, Target, BookOpen, CheckCircle, Maximize2, Minimize2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { buildCourseJsonLd } from '../lib/schemaOrg';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const languageFlags = {
  at: '🇦🇹', de: '🇩🇪', en: '🇬🇧', cz: '🇨🇿', hu: '🇭🇺',
  ro: '🇷🇴', pl: '🇵🇱', fr: '🇫🇷', it: '🇮🇹', zh: '🇨🇳', sl: '🇸🇮',
};

const languageNames = {
  at: { de: 'Deutsch (AT)', en: 'German (AT)' },
  de: { de: 'Deutsch (DE)', en: 'German (DE)' },
  en: { de: 'Englisch', en: 'English' },
  cz: { de: 'Tschechisch', en: 'Czech' },
  hu: { de: 'Ungarisch', en: 'Hungarian' },
  ro: { de: 'Rumänisch', en: 'Romanian' },
  pl: { de: 'Polnisch', en: 'Polish' },
  fr: { de: 'Französisch', en: 'French' },
  it: { de: 'Italienisch', en: 'Italian' },
  zh: { de: 'Chinesisch', en: 'Chinese' },
  sl: { de: 'Slowenisch', en: 'Slovenian' },
};

const legalContextLabels = {
  at: { de: 'Österreich', en: 'Austria' },
  de: { de: 'Deutschland', en: 'Germany' },
  eu: { de: 'EU', en: 'EU' },
  global: { de: 'Global', en: 'Global' },
};

export default function CourseDetailPage() {
  const { id } = useParams();
  const { language } = useLanguage();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDemo, setShowDemo] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/elearnings/${id}`)
      .then(res => setCourse(res.data))
      .catch((error) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch course:', error);
        }
        setCourse(null);
      })
      .finally(() => setLoading(false));
  }, [id]); // BACKEND_URL is constant, axios is stable, setters are stable

  if (loading) return (
    <div className="min-h-screen pt-20 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-[#2E5E99] border-t-transparent rounded-full" />
    </div>
  );

  if (!course) return (
    <div className="min-h-screen pt-20 flex flex-col items-center justify-center">
      <p className="text-[#5A7A9B] text-lg mb-4">{language === 'de' ? 'Kurs nicht gefunden.' : 'Course not found.'}</p>
      <Link to="/produktkatalog"><Button>{language === 'de' ? 'Zurück zum Katalog' : 'Back to Catalog'}</Button></Link>
    </div>
  );

  const demoUrl = course.demoUrl?.[language] || course.demoUrl?.en || course.demoUrl?.de;

  // Generate SEO keywords based on course data
  const courseTitle = course.title?.[language] || course.title?.de || '';
  const courseCategory = course.category?.name?.[language] || course.category?.name?.de || '';
  const seoKeywords = language === 'de'
    ? `${courseTitle}, E-Learning ${courseCategory}, Online Schulung ${courseCategory}, Unterweisung ${courseCategory}, SCORM Modul, Pflichtschulung online, Arbeitssicherheit E-Learning`
    : `${courseTitle}, E-Learning ${courseCategory}, Online Training ${courseCategory}, Instruction ${courseCategory}, SCORM Module, Mandatory Training Online, Workplace Safety E-Learning`;

  const seoDescription = language === 'de'
    ? `${courseTitle} - E-Learning Modul für ${courseCategory}. ${course.description?.[language]?.substring(0, 120) || ''} SCORM-kompatibel, mehrsprachig verfügbar.`
    : `${courseTitle} - E-Learning module for ${courseCategory}. ${course.description?.[language]?.substring(0, 120) || ''} SCORM-compatible, available in multiple languages.`;

  const coursePath = `/course/${course.id}`;
  // Social OG image is served as a static asset from the frontend public
  // root at /og/<id>_<lang>.jpg (NOT /api/static/og/...). Serving it from the
  // static CDN means:
  //   1. No robots.txt Disallow: /api/ interference (Google Images can index).
  //   2. Long immutable cache headers (FB/LI/WA/Slack can cache properly).
  //   3. Zero backend pressure — direct CDN serving.
  // The prerender build step (`scripts/prerender.cjs`) pulls every image
  // from the backend at build time and writes it to build/og/, so the
  // frontend container ships a complete snapshot of all OG cards.
  const ogImage = `https://e-matrix.at/og/${course.id}_${language}.jpg`;
  const courseJsonLd = buildCourseJsonLd({ course, language, path: coursePath, ogImage });

  return (
    <div className="min-h-screen pt-20 bg-[#E7F0FA]" data-testid="course-detail-page">
      <SEO
        title={courseTitle}
        description={seoDescription}
        keywords={seoKeywords}
        path={coursePath}
        lang={language}
        image={ogImage}
        type="article"
        jsonLd={courseJsonLd}
      />
      
      {/* Header */}
      <section className="py-8 bg-gradient-to-br from-[#E7F0FA] via-white to-[#dde9f5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/produktkatalog" className="inline-flex items-center text-[#2E5E99] mb-6 hover:underline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'de' ? 'Zurück zum Katalog' : 'Back to Catalog'}
          </Link>
        </div>
      </section>

      {/* Course Content */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Left - Image + Meta */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="rounded-2xl overflow-hidden shadow-lg mb-6">
                <img src={course.image} alt={course.title?.[language]} className="w-full aspect-square object-cover" loading="lazy" />
              </div>

              {/* Languages */}
              <div className="bg-white rounded-xl p-5 border border-[#C8D8E8] mb-4">
                <h3 className="text-sm font-semibold text-[#0D2440] mb-3">
                  {language === 'de' ? 'Verfügbare Sprachen' : 'Available Languages'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {course.languages?.map(lang => (
                    <span key={lang} className="inline-flex items-center space-x-1 px-3 py-1.5 bg-[#E7F0FA] rounded-full text-sm border border-[#C8D8E8]">
                      <span>{languageFlags[lang]}</span>
                      <span className="text-[#5A7A9B]">{languageNames[lang]?.[language] || lang.toUpperCase()}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Legal Context */}
              {course.legalContext && (
                <div className="bg-white rounded-xl p-5 border border-[#C8D8E8]">
                  <h3 className="text-sm font-semibold text-[#0D2440] mb-3 flex items-center gap-2">
                    <Scale className="w-4 h-4 text-[#2E5E99]" />
                    {language === 'de' ? 'Rechtskontext' : 'Legal Context'}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {course.legalContext.map(ctx => (
                      <span key={ctx} className="inline-flex items-center space-x-1 px-3 py-1.5 bg-[#E7F0FA] rounded-full text-sm border border-[#C8D8E8]">
                        {ctx === 'global' ? <Globe className="w-3.5 h-3.5 text-[#2E5E99]" /> : <span>{languageFlags[ctx] || '🇪🇺'}</span>}
                        <span className="text-[#5A7A9B]">{legalContextLabels[ctx]?.[language] || ctx.toUpperCase()}</span>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-2">
                    {language === 'de'
                      ? 'Gemäß den gesetzlichen Bestimmungen der aufgeführten Länder.'
                      : 'According to the law of the listed countries.'}
                  </p>
                </div>
              )}
            </motion.div>

            {/* Right - Details */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold text-[#0D2440] mb-4" data-testid="course-title">
                {course.title?.[language]}
              </h1>
              <p className="text-lg text-[#5A7A9B] mb-6">
                {(() => {
                  if (typeof course.aboutCourse === 'object') {
                    return course.aboutCourse?.[language];
                  }
                  if (language === 'de') {
                    return course.aboutCourse;
                  }
                  return course.description?.[language];
                })()}
              </p>

              {/* Duration & Target Group */}
              {(course.duration || course.targetGroup) && (
                <div className="flex flex-wrap gap-4 mb-6">
                  {course.duration && (
                    <div className="flex items-center space-x-2 px-4 py-2 bg-[#E7F0FA] rounded-xl border border-[#C8D8E8]">
                      <Clock className="w-4 h-4 text-[#2E5E99]" />
                      <span className="text-sm text-[#0D2440] font-medium">{course.duration}</span>
                    </div>
                  )}
                  {course.targetGroup && (
                    <div className="flex items-center space-x-2 px-4 py-2 bg-[#E7F0FA] rounded-xl border border-[#C8D8E8]">
                      <Target className="w-4 h-4 text-[#2E5E99]" />
                      <span className="text-sm text-[#5A7A9B]">{course.targetGroup?.[language]}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Module Content */}
              {course.moduleContent?.[language] && (
                <div className="bg-white rounded-xl p-6 border border-[#C8D8E8] mb-6" data-testid="module-content">
                  <h3 className="font-semibold text-[#0D2440] mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-[#2E5E99]" />
                    {language === 'de' ? 'Modulinhalte' : 'Module Content'}
                  </h3>
                  <ul className="space-y-2">
                    {course.moduleContent[language].map((item) => (
                      <li key={item} className="flex items-start space-x-3">
                        <CheckCircle className="w-4 h-4 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                        <span className="text-[#5A7A9B] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Learning Objectives */}
              {course.learningObjectives?.[language] && (
                <div className="bg-[#2E5E99]/5 rounded-xl p-6 border border-[#2E5E99]/20 mb-8" data-testid="learning-objectives">
                  <h3 className="font-semibold text-[#0D2440] mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#2E5E99]" />
                    {language === 'de' ? 'Lernziele' : 'Learning Objectives'}
                  </h3>
                  <p className="text-[#5A7A9B] text-sm">{course.learningObjectives[language]}</p>
                </div>
              )}

              {/* Demo Section */}
              {demoUrl && !showDemo && (
                <button
                  onClick={() => setShowDemo(true)}
                  className="w-full bg-[#0D2440] hover:bg-[#162D4A] text-white rounded-2xl p-6 flex items-center justify-between transition-colors mb-8"
                  data-testid="start-demo-btn"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-[#2E5E99] rounded-xl flex items-center justify-center">
                      <Play className="w-7 h-7 text-white fill-white" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-lg">
                        {language === 'de' ? 'Demo starten' : 'Start Demo'}
                      </div>
                      <div className="text-white/60 text-sm">
                        {language === 'de' ? 'Interaktive Vorschau dieses Moduls' : 'Interactive preview of this module'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-white/60" />
                </button>
              )}

              {/* Embedded Demo - Horizontal 16:9 */}
              {showDemo && demoUrl && !fullscreen && (
                <div className="mb-8" data-testid="demo-iframe-container">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-[#0D2440]">
                      {language === 'de' ? 'Interaktive Demo' : 'Interactive Demo'}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setFullscreen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#5A7A9B] hover:text-[#0D2440] rounded-lg border border-[#C8D8E8] hover:bg-[#E7F0FA] transition-colors"
                        title={language === 'de' ? 'Vollbild' : 'Fullscreen'}
                        data-testid="fullscreen-demo-btn"
                      >
                        <Maximize2 className="w-4 h-4" />
                        {language === 'de' ? 'Vollbild' : 'Fullscreen'}
                      </button>
                      <button
                        onClick={() => setShowDemo(false)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#0D2440] hover:bg-[#162D4A] rounded-lg transition-colors"
                        data-testid="close-demo-btn"
                      >
                        <X className="w-4 h-4" />
                        {language === 'de' ? 'Schließen' : 'Close'}
                      </button>
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-[#C8D8E8] shadow-lg bg-white aspect-video">
                    <iframe
                      src={demoUrl}
                      title={`Demo: ${course.title?.[language]}`}
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              {/* Fullscreen Demo Overlay */}
              {showDemo && demoUrl && fullscreen && (
                <>
                  <style>{`
                    [class*="emergent"], iframe[src*="emergent"], 
                    [data-testid="scroll-to-top-btn"],
                    .chat-widget-container { display: none !important; }
                  `}</style>
                  <div className="fixed inset-0 z-[2147483647] bg-black" data-testid="demo-fullscreen">
                    <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                      <button
                        onClick={() => setFullscreen(false)}
                        className="bg-[#0D2440] hover:bg-[#162D4A] text-white p-2.5 rounded-xl shadow-lg transition-colors"
                        title={language === 'de' ? 'Vollbild beenden' : 'Exit Fullscreen'}
                        data-testid="exit-fullscreen-btn"
                      >
                        <Minimize2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => { setFullscreen(false); setShowDemo(false); }}
                        className="bg-[#0D2440] hover:bg-[#162D4A] text-white p-2.5 rounded-xl shadow-lg transition-colors"
                        data-testid="close-fullscreen-btn"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <iframe
                      src={demoUrl}
                      title={`Demo: ${course.title?.[language]}`}
                      className="w-full h-full border-0"
                      allowFullScreen
                    />
                  </div>
                </>
              )}

              {/* CTA */}
              <Link to="/booking" className="block mb-8">
                <Button className="w-full bg-[#2E5E99] hover:bg-[#1E4F85] text-white h-14 text-base">
                  {language === 'de' ? 'Demo / Anfrage' : 'Demo / Inquiry'}
                </Button>
              </Link>

              <div className="flex gap-4">
                <Link to="/contact?type=trial" className="flex-1">
                  <Button className="w-full bg-[#0D2440] hover:bg-[#162D4A] text-white h-12">
                    {language === 'de' ? 'Testversion anfragen' : 'Request Trial'}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
